import React, { useEffect, useState } from 'react';
import { SignalRProvider } from '../../context/SignalRContext';
import { useAuth } from '../../auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import UserManagement from '../../components/UserManagement';
import MenuManagement from '../../components/MenuManagement';
import TableManagement from '../../components/TableManagement';
import QuickServe from '../../components/quick-serve/QuickServe';
import LiveOrderTracking from '../../components/LiveOrderTracking';
import AnalyticsDashboard from '../../components/AnalyticsDashboard';
import { Menu, X, Sun, Moon, LogOut } from 'lucide-react';
import { getAllowedTabsFromToken } from "../../utils/jwtHelper";
import BillGeneration from '../../components/BillGeneration';
import OrderHistoryForRestaurant from '../../components/OrderHistoryForRestaurant';
import TableTracking from '../../components/TableTracking';
import RestaurantSettings from '../../components/RestaurantSettings/RestaurantSettings';
import { ScreenAccessKey as TabOption } from '../../constants/screenAccess';

const sidebarItems: { label: string; tab: TabOption }[] = [
  { label: 'Live Orders', tab: 'orders' },
  { label: 'Bill Generation', tab: 'billGeneration' },
  { label: 'Order History', tab: 'orderHistoryForRestaurant' },
  { label: 'Table Tracking', tab: 'tableTracking' },
  { label: 'Quick Serve', tab: 'quickServe' },
  { label: 'Analytics', tab: 'analytics' },
  { label: 'User Management', tab: 'userManagement' },
  { label: 'Menu Management', tab: 'menu' },
  { label: 'Table Management', tab: 'tables' },
  { label: 'Settings', tab: 'restaurantSettings' },
];

let RESTAURANT_NAME = localStorage.getItem('Dinelyt_session')
  ? JSON.parse(localStorage.getItem('Dinelyt_session') ?? '{}').restaurantName
  : 'Dinelyt Admin';

let RESTAURANT_LOGO_URL = localStorage.getItem('restaurantLogoUrl')
  ? localStorage.getItem('Dinelyt_session')
    ? JSON.parse(localStorage.getItem('Dinelyt_session') ?? '{}').restaurantLogoUrl
    : ''
  : '';

const AdminDashboard: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [allowedTabs, setAllowedTabs] = useState<TabOption[]>([]);
  const [activeTab, setActiveTab] = useState<TabOption | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedTheme = localStorage.getItem('theme');
      if (storedTheme !== null) {
        return storedTheme === 'dark';
      }
      return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  RESTAURANT_NAME = localStorage.getItem('Dinelyt_session')
    ? JSON.parse(localStorage.getItem('Dinelyt_session') ?? '{}').restaurantName
    : 'Dinelyt Admin';

  RESTAURANT_LOGO_URL = localStorage.getItem('restaurantLogoUrl')
    ? localStorage.getItem('Dinelyt_session')
      ? JSON.parse(localStorage.getItem('Dinelyt_session') ?? '{}').restaurantLogoUrl
      : ''
    : '';

  useEffect(() => {
    const tabs = getAllowedTabsFromToken();
    setAllowedTabs(tabs as TabOption[]);
    const savedTab = localStorage.getItem('selectedTab') as TabOption;
    if (savedTab && tabs.includes(savedTab)) {
      setActiveTab(savedTab);
      return;
    }
    if (tabs.length > 0) {
      setActiveTab(tabs[0] as TabOption);
    }
  }, []);

  const accessibleSidebarItems = sidebarItems.filter(item =>
    allowedTabs.includes(item.tab)
  );

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  const handleLogout = () => {
    logout();
    navigate('/restaurant/login');
  };

  const renderTab = () => {
    if (!activeTab || !allowedTabs.includes(activeTab)) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
          <h2 className="text-2xl font-bold mb-4">Unauthorized Access</h2>
          <p className="text-lg">You are not authorized to view this screen. Please select an available tab.</p>
        </div>
      );
    }
    switch (activeTab) {
      case 'orders':
        return <LiveOrderTracking isDarkTheme={darkMode} />;
      case 'billGeneration':
        return <BillGeneration />;
      case 'orderHistoryForRestaurant':
        return <OrderHistoryForRestaurant />;
      case 'tableTracking':
        return <TableTracking />;
      case 'quickServe':
        return <QuickServe restaurantGuid={restaurantGuid} />;
      case 'analytics':
        return <AnalyticsDashboard />;
      case 'userManagement':
        return <UserManagement />;
      case 'menu':
        return <MenuManagement />;
      case 'tables':
        return <TableManagement />;
      case 'restaurantSettings':
        return <RestaurantSettings />;
      default:
        return null;
    }
  };

  // Get restaurantGuid from session/localStorage
  const restaurantGuid = localStorage.getItem('restaurantGuid') || (localStorage.getItem('Dinelyt_session') ? JSON.parse(localStorage.getItem('Dinelyt_session') ?? '{}').restaurantGuid : '');
  const userRole = localStorage.getItem('Dinelyt_session') ? JSON.parse(localStorage.getItem('Dinelyt_session') ?? '{}').role : '';
  // You may want to set hubUrl from config/env
  const hubUrl = `${process.env.REACT_APP_API_BASE || 'https://localhost:44381'}/restaurantHub?restaurantGuid=${restaurantGuid}&role=${userRole}`;
  const groupName = `restaurant-${restaurantGuid}`;

  return (
    <SignalRProvider hubUrl={hubUrl} groupName={groupName} userRole={userRole} restaurantGuid={restaurantGuid}>
      <div className="flex w-full bg-gray-100 text-gray-900 dark:bg-gray-900 transition-colors duration-300 font-inter min-h-0">

        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-gray-900 bg-opacity-50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}

        {/* Sidebar */}
        <nav className={`
        fixed inset-y-0 left-0 z-50
        flex w-64 flex-col
        bg-white p-4 shadow-md transition-transform duration-300 ease-in-out
        dark:bg-gray-800
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
          <div className="flex items-center justify-between mb-6 lg:justify-start flex-shrink-0">
            <div className="flex items-center">
              {RESTAURANT_LOGO_URL && (
                <img
                  src={RESTAURANT_LOGO_URL}
                  alt="Restaurant Logo"
                  className="h-10 w-10 rounded-full"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              )}
              <h1 className="text-xl font-bold ml-3 text-gray-800 dark:text-gray-100">{RESTAURANT_NAME}</h1>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-full text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 lg:hidden"
              title="Close Menu"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="flex flex-col space-y-2 flex-grow">
            {accessibleSidebarItems.map((item) => (
              <button
                key={item.tab}
                className={`
                py-3 px-4 text-left rounded-lg font-medium transition-colors duration-200
                ${activeTab === item.tab
                    ? 'bg-orange-600 text-white shadow-lg'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }
              `}
                onClick={() => {
                  setActiveTab(item.tab);
                  localStorage.setItem('selectedTab', item.tab);
                  setSidebarOpen(false);
                }}
              >
                {item.label}
              </button>
            ))}
          </div>

          <button
            onClick={handleLogout}
            className="mt-4 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold bg-orange-500 text-white hover:bg-orange-700 transition-colors duration-200 flex-shrink-0"
          >
            <LogOut className="h-5 w-5" />
            <span>Logout</span>
          </button>
        </nav>

        {/* Main Content */}
        <main className="flex-1 flex flex-col lg:ml-64 bg-gray-100 dark:bg-gray-900 w-full min-h-0">
          <header className="sticky top-0 z-30 flex items-center justify-between bg-gray-100 p-4 dark:bg-gray-900 lg:hidden shadow-sm flex-shrink-0">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              title="Open Menu"
            >
              <Menu className="h-6 w-6" />
            </button>
            <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100">{RESTAURANT_NAME}</h1>
            <button
              className="p-2 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
              onClick={() => setDarkMode(d => !d)}
              title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {darkMode ? <Sun className="h-6 w-6 text-yellow-400" /> : <Moon className="h-6 w-6" />}
            </button>
          </header>

          {/* Scrollable content area */}
          <div className="pt-0 md:px-6 lg:px-8 pb-4 md:pb-6 lg:pb-8 w-full flex-1 overflow-auto">
            {accessibleSidebarItems.length > 0 ? (
              renderTab()
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 dark:text-gray-400">
                <h2 className="text-3xl font-bold mb-4">Unauthorized Access</h2>
                <p className="text-lg">You are not authorized to see any screen. Please contact an administrator.</p>
              </div>
            )}
          </div>
        </main>

        {/* Desktop Dark Mode Toggle */}
        <div className="fixed top-2 right-2 z-50 hidden lg:block">
          <button
            className="p-2 rounded-full border border-gray-300 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
            onClick={() => setDarkMode(d => !d)}
            title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {darkMode ? <Sun className="h-6 w-6 text-yellow-400" /> : <Moon className="h-6 w-6 text-gray-600" />}
          </button>
        </div>
      </div>
    </SignalRProvider>
  );
};

export default AdminDashboard;
